import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Bell, BellOff, Plus, Trash2 } from 'lucide-react'

import {
  useBoatWantMatches,
  useBoatWants,
  useDeleteBoatWant,
  useUpdateBoatWant,
} from '@/features/customers/boat-want-hooks'
import { boatMatchName, wantCriteriaChips } from '@/features/customers/boat-want-utils'
import { BoatWantDialog } from '@/features/customers/components/boat-want-dialog'
import { formatCurrency } from '@/lib/format'
import type { BoatWant } from '@/types/boat-want'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

interface BoatWantsCardProps {
  customerId: string
  customerFirstName?: string
  customerPhone?: string | null
}

// "Looking For" card on the customer detail page: what the customer is
// shopping for, live inventory matches, and optional text alerts.
export function BoatWantsCard({
  customerId,
  customerFirstName,
  customerPhone,
}: BoatWantsCardProps) {
  const { data: wants = [], isLoading } = useBoatWants(customerId)
  const updateMutation = useUpdateBoatWant()
  const deleteMutation = useDeleteBoatWant()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<BoatWant | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const toggleNotify = (want: BoatWant) => {
    updateMutation.mutate({
      id: want.id,
      body: {
        raw_text: want.raw_text,
        notify_enabled: !want.notify_enabled,
        criteria: want.criteria,
      },
    })
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Looking For</CardTitle>
          <CardDescription>
            What {customerFirstName || 'this customer'} is shopping for, matched against current
            inventory.
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setEditing(null)
            setDialogOpen(true)
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> Add
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : wants.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No boat wants yet. Capture what they're looking for and get notified when a matching
            boat arrives.
          </p>
        ) : (
          <div className="space-y-3">
            {wants.map((want) => {
              const chips = wantCriteriaChips(want.criteria)
              const expanded = expandedId === want.id
              return (
                <div key={want.id} className="rounded-lg border">
                  <div className="flex flex-wrap items-start justify-between gap-3 p-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm italic">“{want.raw_text}”</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {chips.length > 0 ? (
                          chips.map((chip) => (
                            <Badge key={chip} variant="outline" className="text-[11px] font-normal">
                              {chip}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            No criteria detected
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        title={
                          want.notify_enabled
                            ? 'Text alerts on — click to turn off'
                            : 'Text alerts off — click to enable'
                        }
                        disabled={!customerPhone || updateMutation.isPending}
                        onClick={() => toggleNotify(want)}
                      >
                        {want.notify_enabled ? (
                          <Bell className="h-4 w-4 text-primary" />
                        ) : (
                          <BellOff className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedId(expanded ? null : want.id)}
                      >
                        Matches
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditing(want)
                          setDialogOpen(true)
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Delete"
                        disabled={deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate(want.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {expanded && <WantMatches wantId={want.id} />}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>

      <BoatWantDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        customerId={customerId}
        customerFirstName={customerFirstName}
        customerPhone={customerPhone}
        want={editing}
      />
    </Card>
  )
}

function WantMatches({ wantId }: { wantId: string }) {
  const { data: matches = [], isLoading } = useBoatWantMatches(wantId)

  if (isLoading) {
    return (
      <div className="space-y-2 border-t p-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }
  if (matches.length === 0) {
    return (
      <div className="border-t p-3 text-sm text-muted-foreground">
        Nothing in current inventory right now — the customer will be texted when a match arrives
        (if alerts are on).
      </div>
    )
  }
  return (
    <div className="space-y-2 border-t p-3">
      {matches.map((m) => (
        <Link
          key={m.boat_id}
          to="/inventory/$boatId"
          params={{ boatId: m.boat_id }}
          className="group block"
        >
          <div className="flex items-center justify-between gap-3 rounded-md p-2 transition-colors group-hover:bg-muted/50">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{boatMatchName(m)}</div>
              <div className="text-xs text-muted-foreground">
                {[
                  m.length_ft > 0 ? `${m.length_ft} ft` : null,
                  m.engine_hours != null ? `${m.engine_hours} hrs` : null,
                  m.reasons[0],
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <span className="shrink-0 text-sm tabular-nums">{formatCurrency(m.price)}</span>
          </div>
        </Link>
      ))}
    </div>
  )
}
